import React, { useState, useEffect } from 'react';
import { fetchSchedules, createSchedule, updateSchedule, deleteSchedule, toggleSchedule, fetchSuppliers } from '../services/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Play } from 'lucide-react';
import { tasksApi, factoriesApi } from '../services/api';
import './SchedulePage.css';

const DAYS_OF_WEEK = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];

function SchedulePage() {
  const [schedules, setSchedules] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [factories, setFactories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const queryClient = useQueryClient();

  // Мутация для запуска расписания
  const runScheduleMutation = useMutation({
    mutationFn: (scheduleId) => tasksApi.runSchedule(scheduleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast.success('Расписание запущено');
    },
    onError: () => toast.error('Ошибка запуска расписания'),
  });

  // Форма
  const [formData, setFormData] = useState({
    name: '',
    frequency: 'daily',
    day_of_week: 0,
    time1: '23:00',
    time2: '',
    task_type: 'update',
    supplier_ids: [],
    factory_ids: [],
    batch_size: 0,
    enabled: true,
    website_linked_filter: null,
    only_changed_prices: false,
    notify_telegram_chat_ids: [],
    notify_check_types: [],
    feed_url: '',
  });

  useEffect(() => {
    loadSchedules();
    loadSuppliers();
    loadFactories();
  }, []);

  const loadSchedules = async () => {
    try {
      const data = await fetchSchedules();
      setSchedules(data);
    } catch (err) {
      setError('Ошибка загрузки расписаний');
      console.error(err);
    }
  };

  const loadSuppliers = async () => {
    try {
      const data = await fetchSuppliers();
      setSuppliers(data);
    } catch (err) {
      console.error('Ошибка загрузки поставщиков:', err);
    }
  };

  const loadFactories = async () => {
    try {
      const res = await factoriesApi.getAll();
      setFactories(res.data || []);
    } catch (err) {
      console.error('Ошибка загрузки фабрик:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Подготавливаем данные для отправки
      const submitData = {
        ...formData,
        time2: (formData.frequency === 'daily' || formData.frequency === 'weekly') ? null : (formData.time2 || null),
        day_of_week: formData.frequency === 'weekly' ? Number(formData.day_of_week) : null,
        supplier_ids: (formData.task_type === 'send_prices' || formData.task_type === 'import_feed' || formData.task_type === 'factory_sync') ? [] : formData.supplier_ids,
        factory_ids: formData.task_type === 'factory_sync' ? formData.factory_ids : [],
        notify_telegram_chat_ids: formData.notify_telegram_chat_ids || [],
        notify_check_types: formData.notify_check_types || [],
      };
      
      if (editingSchedule) {
        await updateSchedule(editingSchedule.id, submitData);
      } else {
        await createSchedule(submitData);
      }
      
      loadSchedules();
      resetForm();
    } catch (err) {
      console.error('Ошибка сохранения расписания:', err);
      
      // Обработка разных типов ошибок
      let errorMessage = 'Ошибка сохранения расписания';
      
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        // Если detail - это строка, используем её
        if (typeof detail === 'string') {
          errorMessage = detail;
        }
        // Если detail - это массив ошибок валидации Pydantic
        else if (Array.isArray(detail)) {
          errorMessage = detail.map(e => {
            const field = e.loc?.join('.') || 'поле';
            return `${field}: ${e.msg}`;
          }).join('; ');
        }
        // Если detail - это объект
        else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (schedule) => {
    setEditingSchedule(schedule);
    setFormData({
      name: schedule.name,
      frequency: schedule.frequency,
      day_of_week: schedule.day_of_week ?? 0,
      time1: schedule.time1,
      time2: schedule.time2 || '',
      task_type: schedule.task_type || 'update',
      supplier_ids: schedule.supplier_ids || [],
      factory_ids: schedule.factory_ids || [],
      batch_size: schedule.batch_size || 0,
      enabled: schedule.enabled,
      website_linked_filter: schedule.website_linked_filter !== undefined ? schedule.website_linked_filter : null,
      only_changed_prices: schedule.only_changed_prices || false,
      notify_telegram_chat_ids: schedule.notify_telegram_chat_ids || [],
      notify_check_types: schedule.notify_check_types || [],
      feed_url: schedule.feed_url || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить расписание?')) return;

    try {
      await deleteSchedule(id);
      loadSchedules();
    } catch (err) {
      setError('Ошибка удаления расписания');
      console.error(err);
    }
  };

  const handleToggle = async (id) => {
    try {
      await toggleSchedule(id);
      loadSchedules();
    } catch (err) {
      setError('Ошибка изменения статуса расписания');
      console.error(err);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      frequency: 'daily',
      day_of_week: 0,
      time1: '23:00',
      time2: '',
      task_type: 'update',
      supplier_ids: [],
      factory_ids: [],
      batch_size: 0,
      enabled: true,
      website_linked_filter: null,
      only_changed_prices: false,
      notify_telegram_chat_ids: [],
      notify_check_types: [],
      feed_url: '',
    });
    setEditingSchedule(null);
    setShowForm(false);
  };

  const handleSupplierToggle = (supplierId) => {
    setFormData(prev => ({
      ...prev,
      supplier_ids: prev.supplier_ids.includes(supplierId)
        ? prev.supplier_ids.filter(id => id !== supplierId)
        : [...prev.supplier_ids, supplierId]
    }));
  };

  const formatLastRun = (lastRun) => {
    if (!lastRun) return 'Никогда';
    const date = new Date(lastRun);
    return date.toLocaleString('ru-RU');
  };

  const getSupplierNames = (supplierIds) => {
    return supplierIds
      .map(id => suppliers.find(s => s.id === id)?.name || `ID: ${id}`)
      .join(', ');
  };

  return (
    <div className="schedule-page">
      <div className="page-header">
        <h1>Расписание обновлений</h1>
        <button 
          className="btn btn-primary"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Отмена' : '+ Создать расписание'}
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {showForm && (
        <div className="schedule-form-container">
          <h2>{editingSchedule ? 'Редактировать расписание' : 'Новое расписание'}</h2>
          <form onSubmit={handleSubmit} className="schedule-form">
            <div className="form-group">
              <label>Название:</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Например: Обновление вечером"
                required
              />
            </div>

            <div className="form-group">
              <label>Тип задачи:</label>
              <select
                value={formData.task_type}
                onChange={(e) => setFormData({...formData, task_type: e.target.value})}
              >
                <option value="update">Обновление от поставщика</option>
                <option value="send_remains">Отправка остатков на сайт</option>
                <option value="send_prices">Отправка цен на сайт</option>
                <option value="notify">Уведомление о проблемах</option>
                <option value="import_feed">Импорт feed.xml</option>
                <option value="factory_sync">Запросы на фабрику</option>
              </select>
            </div>

            {formData.task_type === 'import_feed' && (
              <div className="form-group">
                <label>URL для скачивания feed.xml:</label>
                <input
                  type="url"
                  value={formData.feed_url || ''}
                  onChange={(e) => setFormData({...formData, feed_url: e.target.value})}
                  placeholder="https://example.com/feed.xml"
                  required
                />
                <small style={{ color: '#6b7280', marginTop: '4px', display: 'block' }}>
                  Укажите URL для скачивания feed.xml файла. Будет обновлять website_url и website_image товаров.
                </small>
              </div>
            )}

            {(formData.task_type === 'send_remains' || formData.task_type === 'send_prices') && (
              <div className="form-group">
                <label>Размер пакета для отправки:</label>
                <input
                  type="number"
                  min="0"
                  value={formData.batch_size}
                  onChange={(e) => setFormData({...formData, batch_size: parseInt(e.target.value) || 0})}
                  placeholder="0 = отправить все сразу"
                />
                <small style={{ color: '#6b7280', marginTop: '4px', display: 'block' }}>
                  {formData.task_type === 'send_remains' 
                    ? 'Если 0, отправляем все остатки одним запросом. Если указано число (например, 2000), делим на пакеты.'
                    : 'Если 0, отправляем все измененные цены одним запросом. Если указано число (например, 1000), делим на пакеты.'}
                </small>
              </div>
            )}

            <div className="form-group">
              <label>Частота:</label>
              <select
                value={formData.frequency}
                onChange={(e) => {
                  const newFrequency = e.target.value;
                  setFormData({
                    ...formData, 
                    frequency: newFrequency,
                    // Очищаем time2 при переключении на daily
                    time2: newFrequency === 'daily' ? '' : formData.time2
                  });
                }}
              >
                <option value="daily">1 раз в день</option>
                <option value="twice_daily">2 раза в день</option>
                <option value="weekly">1 раз в неделю</option>
              </select>
            </div>

            {formData.frequency === 'weekly' && (
              <div className="form-group">
                <label>День недели:</label>
                <select
                  value={formData.day_of_week}
                  onChange={(e) => setFormData({...formData, day_of_week: Number(e.target.value)})}
                >
                  {DAYS_OF_WEEK.map((day, i) => (
                    <option key={i} value={i}>{day}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Время:</label>
                <input
                  type="time"
                  value={formData.time1}
                  onChange={(e) => setFormData({...formData, time1: e.target.value})}
                  required
                />
              </div>

              {formData.frequency === 'twice_daily' && (
                <div className="form-group">
                  <label>Время 2:</label>
                  <input
                    type="time"
                    value={formData.time2}
                    onChange={(e) => setFormData({...formData, time2: e.target.value})}
                    required
                  />
                </div>
              )}
            </div>

            {formData.task_type === 'notify' && (
              <div className="form-group">
                <label style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>Типы проверок:</label>
                {[
                  { id: 'no_update',     label: 'Ошибка при обновлении поставщика' },
                  { id: 'zero_stock',    label: 'Нулевой остаток у поставщика' },
                  { id: 'send_error',    label: 'Ошибка при отправке на сайт' },
                  { id: '1c_error',      label: 'Ошибка при загрузке цен из 1С' },
                  { id: 'overdue',       label: 'Давно не обновлялся (просрочен срок)' },
                  { id: 'factory_query', label: 'Проверка запросов фабрик (бот)' },
                ].map(ct => (
                  <label key={ct.id} className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                    <input
                      type="checkbox"
                      checked={formData.notify_check_types.includes(ct.id)}
                      onChange={(e) => {
                        const updated = e.target.checked
                          ? [...formData.notify_check_types, ct.id]
                          : formData.notify_check_types.filter(x => x !== ct.id);
                        setFormData({...formData, notify_check_types: updated});
                      }}
                    />
                    <span>{ct.label}</span>
                  </label>
                ))}
              </div>
            )}

            {formData.task_type === 'notify' && (
              <div className="form-group">
                <label style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>
                  Telegram chat_id получателей:
                </label>
                <input
                  type="text"
                  placeholder="Например: 123456789, 987654321"
                  value={(formData.notify_telegram_chat_ids || []).join(', ')}
                  onChange={(e) => {
                    const ids = e.target.value
                      .split(',')
                      .map(s => s.trim())
                      .filter(Boolean);
                    setFormData({...formData, notify_telegram_chat_ids: ids});
                  }}
                  style={{ width: '100%' }}
                />
                <small style={{ color: '#6b7280', display: 'block', marginTop: '4px' }}>
                  Введите Telegram chat_id через запятую. Узнать свой chat_id: напишите боту, затем откройте
                  {' '}<a href="https://t.me/userinfobot" target="_blank" rel="noreferrer">@userinfobot</a>
                </small>
                {(formData.notify_telegram_chat_ids || []).length === 0 && (
                  <small className="error-text">Введите хотя бы один chat_id</small>
                )}
              </div>
            )}

            {!['send_prices', 'import_feed', 'factory_sync'].includes(formData.task_type) && (
              <div className="form-group">
                <label>Поставщики{formData.task_type === 'notify' ? ' (опционально — оставьте пустым для проверки всех)' : ''}:</label>
                <div className="suppliers-list">
                  {suppliers
                    .filter(supplier => formData.task_type !== 'update' || !supplier.self_upload)
                    .map(supplier => (
                    <label key={supplier.id} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.supplier_ids.includes(supplier.id)}
                        onChange={() => handleSupplierToggle(supplier.id)}
                      />
                      <span>{supplier.name}</span>
                    </label>
                  ))}
                </div>
                {formData.supplier_ids.length === 0 && formData.task_type !== 'notify' && (
                  <small className="error-text">Выберите хотя бы одного поставщика</small>
                )}
              </div>
            )}

            {formData.task_type === 'factory_sync' && (
              <div className="form-group">
                <label>Фабрики:</label>
                <div className="suppliers-list">
                  {factories.filter(f => f.integration_type).map(factory => (
                    <label key={factory.id} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={(formData.factory_ids || []).includes(factory.id)}
                        onChange={() => {
                          const ids = formData.factory_ids || [];
                          setFormData({...formData, factory_ids: ids.includes(factory.id)
                            ? ids.filter(id => id !== factory.id)
                            : [...ids, factory.id]
                          });
                        }}
                      />
                      <span>{factory.name}</span>
                    </label>
                  ))}
                  {factories.filter(f => f.integration_type).length === 0 && (
                    <small style={{ color: '#9ca3af' }}>Нет фабрик с настроенной интеграцией</small>
                  )}
                </div>
                {(formData.factory_ids || []).length === 0 && (
                  <small className="error-text">Выберите хотя бы одну фабрику</small>
                )}
              </div>
            )}

            {formData.task_type === 'send_prices' && (
              <div className="form-group">
                <label style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>Дополнительные фильтры:</label>
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#374151' }}>Связка с сайтом:</label>
                  <select
                    value={formData.website_linked_filter === null ? '' : formData.website_linked_filter === true ? 'true' : 'false'}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFormData({...formData, website_linked_filter: v === '' ? null : v === 'true'});
                    }}
                    style={{ width: '100%' }}
                  >
                    <option value="">Все товары</option>
                    <option value="true">Только товары со связкой с сайтом</option>
                    <option value="false">Только товары БЕЗ связки с сайтом</option>
                  </select>
                </div>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.only_changed_prices}
                    onChange={(e) => setFormData({...formData, only_changed_prices: e.target.checked})}
                  />
                  <span>Только изменённые цены (дата изменения &gt; даты выгрузки)</span>
                </label>
              </div>
            )}

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.enabled}
                  onChange={(e) => setFormData({...formData, enabled: e.target.checked})}
                />
                <span>Включено</span>
              </label>
            </div>

            <div className="form-actions">
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading ||
                  (formData.task_type === 'import_feed' && !formData.feed_url) ||
                  (formData.task_type === 'factory_sync' && (formData.factory_ids || []).length === 0) ||
                  (['update', 'send_remains'].includes(formData.task_type) && formData.supplier_ids.length === 0) ||
                  (formData.task_type === 'notify' && (formData.notify_check_types.length === 0 || (formData.notify_telegram_chat_ids || []).length === 0))
                }
              >
                {loading ? 'Сохранение...' : 'Сохранить'}
              </button>
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={resetForm}
              >
                Отмена
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="schedules-list">
        <h2>Текущие расписания</h2>
        {schedules.length === 0 ? (
          <p className="empty-message">Нет созданных расписаний</p>
        ) : (
          <table className="schedules-table">
            <thead>
              <tr>
                <th>Название</th>
                <th>Тип</th>
                <th>Частота</th>
                <th>Время</th>
                <th>Поставщики</th>
                <th>Последний запуск</th>
                <th>Статус</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {schedules.map(schedule => (
                <tr 
                  key={schedule.id} 
                  className={!schedule.enabled ? 'disabled' : ''}
                  onClick={() => handleEdit(schedule)}
                  style={{ cursor: 'pointer' }}
                  onDoubleClick={(e) => e.stopPropagation()}
                >
                  <td>{schedule.name}</td>
                  <td>
                    <span className={`task-type-badge ${schedule.task_type}`}>
                      {schedule.task_type === 'update'
                        ? '🔄 Обновление'
                        : schedule.task_type === 'send_remains'
                        ? '📤 Остатки'
                        : schedule.task_type === 'notify'
                        ? '🔔 Уведомление'
                        : schedule.task_type === 'import_feed'
                        ? '📥 Импорт feed.xml'
                        : schedule.task_type === 'factory_sync'
                        ? '🏭 Фабрика'
                        : '💰 Цены'}
                    </span>
                    {schedule.task_type === 'send_prices' && (
                      <div style={{ marginTop: '4px', fontSize: '11px', color: '#6b7280' }}>
                        {schedule.website_linked_filter === true && <div>🔗 Только со связкой</div>}
                        {schedule.website_linked_filter === false && <div>🚫 Только без связки</div>}
                        {schedule.only_changed_prices && <div>🔄 Только изменённые</div>}
                      </div>
                    )}
                  </td>
                  <td>
                    {schedule.frequency === 'daily' ? '1 раз в день'
                      : schedule.frequency === 'twice_daily' ? '2 раза в день'
                      : `Еженедельно (${DAYS_OF_WEEK[schedule.day_of_week] ?? '?'})`}
                  </td>
                  <td>
                    {schedule.time1}
                    {schedule.frequency === 'twice_daily' && `, ${schedule.time2}`}
                  </td>
                  <td className="suppliers-cell">
                    {schedule.task_type === 'send_prices'
                      ? '— вся база —'
                      : schedule.task_type === 'import_feed'
                      ? (schedule.feed_url ? <span style={{fontSize: '11px', color: '#6b7280'}}>{schedule.feed_url}</span> : '—')
                      : schedule.task_type === 'notify'
                      ? (schedule.supplier_ids && schedule.supplier_ids.length > 0 ? getSupplierNames(schedule.supplier_ids) : '— все активные —')
                      : schedule.task_type === 'factory_sync'
                      ? (schedule.factory_ids || []).map(id => factories.find(f => f.id === id)?.name || `ID:${id}`).join(', ') || '—'
                      : getSupplierNames(schedule.supplier_ids)}
                  </td>
                  <td>{formatLastRun(schedule.last_run)}</td>
                  <td>
                    <span className={`status-badge ${schedule.enabled ? 'active' : 'inactive'}`}>
                      {schedule.enabled ? 'Включено' : 'Выключено'}
                    </span>
                  </td>
                  <td className="actions-cell">
                    <button
                      className="btn btn-sm btn-success"
                      onClick={(e) => {
                        e.stopPropagation();
                        runScheduleMutation.mutate(schedule.id);
                      }}
                      disabled={runScheduleMutation.isPending}
                      title="Запустить расписание"
                    >
                      <Play size={14} />
                    </button>
                    <button
                      className="btn btn-sm btn-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(schedule.id);
                      }}
                      title="Удалить"
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default SchedulePage;
